/** @jsx jsx */
import React, { useMemo } from "react";
import { jsx } from "theme-ui";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { navigate, Link } from "gatsby";
import handleError from "sites-common/utils/handleError";
import { compareDateWithCurrentDate } from "sites-common/utils/FormatDate";
import { copyKeys } from "sites-common/utils/copyKeys";
import { DefaultButton } from "office-ui-fabric-react";
import NewRegistration from "./NewRegistration";
import {
  hideParticipantsPanel,
  resetParticipantsPanel,
  resetSubmitParticipants,
} from "../../state/actions/participantsPanel";
import { showAlert } from "../../state/actions";
import { useFetchSrcmApi } from "../../fetch-srcm-api";
import { regLimitCb } from "./useNewRegistrationSubmit";
// const ONLY_MY_REGS = "only-my-regs";
// const ONLY_NEW_REG = "only-new-reg";
// const ALL = "all";
// const SMART = "smart";
const regJsonFields = ["gov_id_type", "gov_id", "alternate_phone"];

export const displayEventStatus = (event, styleProp = {}) => {
  const currentDate = new Date().getTime();
  if (!event) {
    return "";
  }
  const regStart = event?.reg_starts
    ? new Date(new Date(event.reg_starts)).getTime()
    : 0;
  const regEnd = event?.reg_ends
    ? new Date(new Date(event.reg_ends)).getTime()
    : 0;
  const eventEnd = event?.end_datetime
    ? new Date(new Date(event.end_datetime)).getTime()
    : 0;
  let msg = "";
  let name = "";

  if (currentDate < regStart) {
    msg = "Comming soon";
    name = "comming_soon_btn";
  }

  if (currentDate > regEnd && currentDate < eventEnd) {
    msg = "Registrations closed";
    name = "reg_closed_btn";
  }
  if (currentDate > eventEnd && eventEnd) {
    msg = "Event closed";
    name = "event_closed_btn";
  }
  return (
    <DefaultButton
      sx={{
        ...(styleProp[name] || {}),
        // display: "block",
        // marginInline: "auto",
        // margin: "12px auto",
      }}
      disabled
    >
      {msg}
    </DefaultButton>
  );
};

const EventRegistrations = ({
  eventId,
  styleProp,
  computePnrPageUrl,
  noUid,
  customConfig,
  data,
  redirectLink,
  registerBtnText,
}) => {
  const { fetchSrcmApi } = useFetchSrcmApi();
  const dispatch = useDispatch();
  const endDate = useMemo(
    () =>
      data?.reg_starts && data?.reg_ends
        ? !compareDateWithCurrentDate(data.reg_starts) &&
          compareDateWithCurrentDate(data.reg_ends)
        : false,
    [data]
  );

  const registerNew = (pnr2, eventId2, persons, p2data) => {
    const defaultRef = "B99999999";
    const childRef = "B99999998";
    const methodParams = persons.map((p0) => {
      let x = {};
      if (p0.ref === defaultRef || p0.ref === childRef) {
        x = {
          gender: p0.gender,
          age_group: p0.age_group,
          city_id: p0.city_id,
          ref: p0.ref,
        };
        if (p0.email) {
          x.email = p0.email;
        }
        if (p0.mobile) {
          x.mobile = p0.mobile;
        }
      } else {
        if (p0?.extras) {
          Object.entries(p0.extras).map(([key, val]) => {
            if (val) {
              x[key] = val;
            }
            return null;
          });
        }
        x.partner_id = p0.id;
      }
      return {
        pnr: pnr2,
        // XXX - hardcoded for now (need to move it to UI element)
        communication_preference: p0.email ? 1 : 0,
        name: p0.name,
        event: eventId2,
        has_registered: true,
        citizen_of_country:
          typeof p0.citizen_of_country === "object"
            ? p0.citizen_of_country?.id
            : p0.citizen_of_country,
        has_oci: p0.has_oci || false,
        reg_json: JSON.stringify(copyKeys(p0, regJsonFields)),
        //
        ...x,
        //
        ...p2data,
      };
    });

    fetchSrcmApi({
      api: `/api/v3/events/${eventId2}/registrations/${
        customConfig?.only_couple_allowed ? "?only_couple_allowed=true" : ""
      }`,
      methodParams,
      client: "eventsClient",
      method: "POST",
    })
      .then((data2) => {
        if (!!data2 && data2.length > 0) {
          const pnr1 = data2[0].pnr;
          dispatch(resetParticipantsPanel());
          dispatch(
            showAlert({
              message: `${
                regLimitCb?.(
                  methodParams,
                  customConfig?.reg_status,
                  data?.reg_needs_approval
                ) || "Registered successfuly"
              }`,
              title: `PNR: ${pnr1}`,
            })
          );
          if (computePnrPageUrl) {
            navigate(computePnrPageUrl({ pnr: pnr1, eventId: eventId2 }));
          }
        } else {
          dispatch(resetSubmitParticipants());
          dispatch(
            showAlert({
              title: "Error",
              message: "Something went wrong. Please try again later.",
            })
          );
        }
      })
      .catch((e) => {
        dispatch(resetSubmitParticipants());
        dispatch(hideParticipantsPanel());
        if (e.toString().includes("exceeds limit")) {
          dispatch(
            showAlert({
              title: "Error",
              message:
                "Maximum registration reached for selected stay preference.",
            })
          );
        } else {
          dispatch(
            showAlert({
              title: "Error",
              message: handleError(e),
            })
          );
        }
      });
  };

  return (
    <React.Fragment>
      {endDate ? (
        <div>
          <NewRegistration
            eventId={eventId}
            registerBtnStyles={{ ...styleProp.registerBtn }}
            computePnrPageUrl={computePnrPageUrl}
            registerNew={registerNew}
            registerBtnText={registerBtnText}
            noUid={noUid}
          />
          {redirectLink && (
            <small>
              Already registered? Go to{" "}
              <Link to={redirectLink}>My registrations</Link>
            </small>
          )}
        </div>
      ) : (
        displayEventStatus(data, styleProp)
      )}
    </React.Fragment>
  );
};

EventRegistrations.defaultProps = {
  styleProp: {},
  computePnrPageUrl: null,
  // displayButtons: SMART,
  noUid: "",
  redirectLink: "",
  customConfig: null,
  registerBtnText: "Register",
};

EventRegistrations.propTypes = {
  eventId: PropTypes.string.isRequired,
  styleProp: PropTypes.shape({
    registerBtn: PropTypes.shape({}),
  }),
  data: PropTypes.shape({
    reg_starts: PropTypes.string,
    reg_ends: PropTypes.string,
    end_datetime: PropTypes.string,
    reg_needs_approval: PropTypes.bool,
  }).isRequired,
  computePnrPageUrl: PropTypes.func,
  // displayButtons: PropTypes.oneOf([SMART, ALL, ONLY_MY_REGS, ONLY_NEW_REG]),
  noUid: PropTypes.string,
  registerBtnText: PropTypes.string,
  customConfig: PropTypes.oneOfType([
    PropTypes.shape({
      reg_status: PropTypes.shape({
        status: PropTypes.string,
        stay_preference: PropTypes.string,
      }),
      only_couple_allowed: PropTypes.bool,
    }),
    PropTypes.shape({
      reg_status: PropTypes.arrayOf({
        status: PropTypes.string,
        stay_preference: PropTypes.string,
      }),
      only_couple_allowed: PropTypes.bool,
    }),
  ]),
  redirectLink: PropTypes.string,
};

export default EventRegistrations;
